/**
 * Hero section — 5 variants (centered, split, editorial, image_bg, event)
 */
import type { CSSProperties, ReactNode } from 'react';
import type { HeroContent } from '../schemas/hero';
import type { BrandContext, SectionStyle } from '../types/index';
import {
  Body,
  Btn,
  Eyebrow,
  H1,
  SectionFrame,
  VisualPlaceholder,
} from './primitives';

interface HeroProps {
  content: HeroContent;
  style?: SectionStyle;
  brand?: BrandContext;
}

/** style.ctaAlign を flex justifyContent に変換（centered のデフォルトは center、その他は flex-start） */
function ctaJustify(
  align: SectionStyle['ctaAlign'] | undefined,
  fallback: 'flex-start' | 'center' | 'flex-end' = 'flex-start',
): 'flex-start' | 'center' | 'flex-end' {
  if (align === 'left') return 'flex-start';
  if (align === 'center') return 'center';
  if (align === 'right') return 'flex-end';
  return fallback;
}

const LOGO_HEIGHT = { sm: 24, md: 36, lg: 56 } as const;

/**
 * bottom-* 位置のロゴを Hero 内右下/左下に絶対配置。
 * top-* と none の場合は null（top-* は renderHeroHeader 側で Eyebrow と統合表示）。
 */
function renderHeroBottomLogo(
  brand: BrandContext | undefined,
  style: SectionStyle | undefined,
  options?: { invertOnDark?: boolean },
): ReactNode {
  const url = brand?.logoUrl;
  if (!url) return null;
  const pos = style?.logoPosition ?? 'top-left';
  if (!pos.startsWith('bottom-')) return null;

  const size = style?.logoSize ?? 'md';
  const height = LOGO_HEIGHT[size];

  const imgStyle: CSSProperties = {
    height,
    width: 'auto',
    display: 'block',
    objectFit: 'contain',
  };
  if (options?.invertOnDark) {
    imgStyle.filter = 'drop-shadow(0 1px 6px rgba(0,0,0,0.3))';
  }

  return (
    <div
      style={{
        position: 'absolute',
        [pos === 'bottom-left' ? 'left' : 'right']: 24,
        bottom: 24,
        zIndex: 5,
      }}
    >
      <img src={url} alt="ロゴ" style={imgStyle} />
    </div>
  );
}

/**
 * Hero ヘッダー行を描画 — Eyebrow と top-* ロゴを同じ高さに集約。
 *
 * レイアウト（3 列 grid: 1fr / auto / 1fr）:
 * - top-left: 左セルに [ロゴ + Eyebrow] を横並び
 * - top-center: 左セルに Eyebrow、中央セルにロゴ
 * - top-right: 左セルに Eyebrow、右セルにロゴ
 * - bottom-* / none: Eyebrow のみ表示（左寄せ）
 */
function renderHeroHeader(
  eyebrowEl: ReactNode | null,
  brand: BrandContext | undefined,
  style: SectionStyle | undefined,
  options?: { invertOnDark?: boolean },
): ReactNode {
  const url = brand?.logoUrl;
  const pos = style?.logoPosition ?? 'top-left';
  const hasTopLogo = !!url && pos.startsWith('top-');

  if (!eyebrowEl && !hasTopLogo) return null;

  const size = style?.logoSize ?? 'md';
  const height = LOGO_HEIGHT[size];

  const logoEl = hasTopLogo ? (
    <img
      src={url ?? undefined}
      alt="ロゴ"
      style={{
        height,
        width: 'auto',
        display: 'block',
        objectFit: 'contain',
        filter: options?.invertOnDark ? 'drop-shadow(0 1px 6px rgba(0,0,0,0.3))' : undefined,
      }}
    />
  ) : null;

  let leftContent: ReactNode = null;
  let centerContent: ReactNode = null;
  let rightContent: ReactNode = null;

  if (pos === 'top-left' && hasTopLogo) {
    // ロゴと Eyebrow を左セルに横並び
    leftContent = (
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        {logoEl}
        {eyebrowEl}
      </div>
    );
  } else if (pos === 'top-center' && hasTopLogo) {
    leftContent = eyebrowEl;
    centerContent = logoEl;
  } else if (pos === 'top-right' && hasTopLogo) {
    leftContent = eyebrowEl;
    rightContent = logoEl;
  } else {
    // ロゴなし or bottom-* / none → Eyebrow のみ左に
    leftContent = eyebrowEl;
  }

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto 1fr',
        alignItems: 'center',
        width: '100%',
        gap: 16,
        minHeight: height,
      }}
    >
      <div style={{ justifySelf: 'start' }}>{leftContent}</div>
      <div style={{ justifySelf: 'center' }}>{centerContent}</div>
      <div style={{ justifySelf: 'end' }}>{rightContent}</div>
    </div>
  );
}

export function HeroCentered({ content, style, brand }: HeroProps) {
  const c = content as HeroContent & {
    eventDate?: string | null;
    eventLocation?: string | null;
    eventPrice?: string | null;
    eventSpeakers?: Array<{ name: string; title?: string | null; avatar?: string | null }> | null;
  };
  const hasBg = !!c.heroImage;
  const eyebrowEl = c.eyebrow ? <Eyebrow>{c.eyebrow}</Eyebrow> : null;

  const inner = (
    <div className="ls-hero-centered" style={{ position: 'relative' }}>
      {renderHeroBottomLogo(brand, style, { invertOnDark: hasBg })}
      {renderHeroHeader(eyebrowEl, brand, style, { invertOnDark: hasBg })}
      <H1
        style={
          hasBg
            ? { color: '#fff', textShadow: '0 1px 12px rgba(0,0,0,0.5)' }
            : undefined
        }
      >
        {c.headline}
      </H1>
      <Body style={hasBg ? { color: 'rgba(255,255,255,0.92)' } : undefined}>
        {c.subheadline}
      </Body>

      {/* イベント詳細メタ (event フィールド入力時のみ) */}
      {(c.eventDate || c.eventLocation || c.eventPrice) && (
        <div
          style={{
            display: 'flex',
            gap: 24,
            flexWrap: 'wrap',
            justifyContent: 'center',
            padding: '16px 20px',
            background: hasBg ? 'rgba(255,255,255,0.1)' : 'var(--ls-surface)',
            border: `1px solid ${hasBg ? 'rgba(255,255,255,0.18)' : 'var(--ls-border)'}`,
            borderRadius: 'var(--ls-radius-md)',
            backdropFilter: hasBg ? 'blur(8px)' : 'none',
            textAlign: 'left',
          }}
        >
          {c.eventDate && <EventMeta label="日時" value={c.eventDate} inverted={hasBg} />}
          {c.eventLocation && <EventMeta label="開催地" value={c.eventLocation} inverted={hasBg} />}
          {c.eventPrice && <EventMeta label="料金" value={c.eventPrice} inverted={hasBg} highlight />}
        </div>
      )}

      {/* 登壇者ロー */}
      {c.eventSpeakers && c.eventSpeakers.length > 0 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          {c.eventSpeakers.slice(0, 6).map((sp, i) => (
            <SpeakerChip key={i} speaker={sp} inverted={hasBg} />
          ))}
        </div>
      )}

      <div className="ls-cta-stack" style={{ justifyContent: ctaJustify(style?.ctaAlign, 'center') }}>
        <Btn variant="primary" href={c.primaryCtaHref ?? '#'}>
          {c.primaryCta}
        </Btn>
        {c.secondaryCta &&
          (hasBg ? (
            <a
              href={c.secondaryCtaHref ?? '#'}
              className="ls-btn"
              style={{
                background: 'transparent',
                color: '#fff',
                border: '1px solid rgba(255,255,255,0.5)',
              }}
            >
              {c.secondaryCta}
            </a>
          ) : (
            <Btn variant="ghost" href={c.secondaryCtaHref ?? '#'}>
              {c.secondaryCta}
            </Btn>
          ))}
      </div>
      {c.trustLine && (
        <Body
          className="ls-trust-line"
          style={{
            fontSize: '0.85rem',
            opacity: 0.7,
            color: hasBg ? '#fff' : undefined,
          }}
        >
          {c.trustLine}
        </Body>
      )}
    </div>
  );

  // 背景画像あり → HeroEvent と同様に半透明オーバーレイ付き背景で全面表示
  if (hasBg) {
    return (
      <section
        className="ls-section"
        style={{
          position: 'relative',
          background: `linear-gradient(180deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.7) 100%), url(${c.heroImage}) center/cover`,
          minHeight: '70vh',
          color: '#fff',
        }}
      >
        <div
          className="ls-section-inner"
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            minHeight: '70vh',
          }}
        >
          {inner}
        </div>
      </section>
    );
  }
  return <SectionFrame>{inner}</SectionFrame>;
}

export function HeroSplit({ content, style, brand }: HeroProps) {
  const eyebrowEl = content.eyebrow ? <Eyebrow>{content.eyebrow}</Eyebrow> : null;
  return (
    <SectionFrame>
      <div className="ls-hero-split" style={{ position: 'relative' }}>
        {renderHeroBottomLogo(brand, style)}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          {renderHeroHeader(eyebrowEl, brand, style)}
          <H1>{content.headline}</H1>
          <Body>{content.subheadline}</Body>
          <div className="ls-cta-stack" style={{ justifyContent: ctaJustify(style?.ctaAlign) }}>
            <Btn variant="primary" href={content.primaryCtaHref ?? '#'}>
              {content.primaryCta}
            </Btn>
            {content.secondaryCta && (
              <Btn variant="ghost" href={content.secondaryCtaHref ?? '#'}>
                {content.secondaryCta}
              </Btn>
            )}
          </div>
          {content.trustLine && (
            <Body style={{ fontSize: '0.85rem', opacity: 0.7 }}>
              {content.trustLine}
            </Body>
          )}
        </div>
        <VisualPlaceholder
          src={content.heroImage}
          alt={content.headline}
          aspectRatio="4 / 3"
          label={content.headline}
        />
      </div>
    </SectionFrame>
  );
}

export function HeroEditorial({ content, style, brand }: HeroProps) {
  const eyebrowEl = content.eyebrow ? <Eyebrow>{content.eyebrow}</Eyebrow> : null;
  return (
    <SectionFrame>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 32,
          alignItems: 'stretch',
          maxWidth: 1100,
          margin: '0 auto',
          position: 'relative',
        }}
      >
        {renderHeroBottomLogo(brand, style)}
        {renderHeroHeader(eyebrowEl, brand, style)}
        <h1
          className="ls-h1"
          style={{
            fontSize: 'clamp(2.5rem, 6vw + 1rem, 6rem)',
            fontFamily: 'var(--ls-font-heading)',
            lineHeight: 1.05,
            letterSpacing: '-0.03em',
          }}
        >
          {content.headline}
        </h1>
        <Body style={{ maxWidth: 640, fontSize: '1.125rem' }}>
          {content.subheadline}
        </Body>
        <div className="ls-cta-stack" style={{ justifyContent: ctaJustify(style?.ctaAlign) }}>
          <Btn variant="primary" href={content.primaryCtaHref ?? '#'}>
            {content.primaryCta}
          </Btn>
          {content.secondaryCta && (
            <Btn variant="ghost" href={content.secondaryCtaHref ?? '#'}>
              {content.secondaryCta}
            </Btn>
          )}
        </div>
        {content.heroImage && (
          <VisualPlaceholder
            src={content.heroImage}
            aspectRatio="21 / 9"
            label={content.headline}
            className="ls-editorial-image"
          />
        )}
      </div>
    </SectionFrame>
  );
}

export function HeroImageBg({ content, style, brand }: HeroProps) {
  const onDark = !!content.heroImage;
  return (
    <section
      className="ls-section"
      style={{
        position: 'relative',
        background: content.heroImage
          ? `linear-gradient(180deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.65) 100%), url(${content.heroImage}) center/cover`
          : `radial-gradient(ellipse at top, var(--ls-surface-alt) 0%, var(--ls-bg) 100%)`,
        minHeight: '70vh',
        color: 'var(--ls-text)',
      }}
    >
      {renderHeroBottomLogo(brand, style, { invertOnDark: onDark })}
      <div
        className="ls-section-inner"
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 24,
          minHeight: '70vh',
          justifyContent: 'flex-end',
          maxWidth: 900,
        }}
      >
        {renderHeroHeader(
          content.eyebrow ? <Eyebrow>{content.eyebrow}</Eyebrow> : null,
          brand,
          style,
          { invertOnDark: onDark },
        )}
        <h1
          className="ls-h1"
          style={{
            color: content.heroImage ? '#fff' : 'var(--ls-text)',
            textShadow: content.heroImage ? '0 1px 12px rgba(0,0,0,0.5)' : 'none',
          }}
        >
          {content.headline}
        </h1>
        <Body
          style={{
            color: content.heroImage ? 'rgba(255,255,255,0.9)' : 'var(--ls-text-muted)',
            maxWidth: 640,
          }}
        >
          {content.subheadline}
        </Body>
        <div className="ls-cta-stack" style={{ justifyContent: ctaJustify(style?.ctaAlign) }}>
          <Btn variant="primary" href={content.primaryCtaHref ?? '#'}>
            {content.primaryCta}
          </Btn>
          {content.secondaryCta && (
            <Btn variant="ghost" href={content.secondaryCtaHref ?? '#'}>
              {content.secondaryCta}
            </Btn>
          )}
        </div>
      </div>
    </section>
  );
}

/**
 * イベント告知用 Hero — 日時 / 開催地 / 料金 / 登壇者バッジを一画面に集約。
 * 背景画像 + 半透明オーバーレイで視認性を確保。
 */
export function HeroEvent({ content, style, brand }: HeroProps) {
  const c = content as HeroContent & {
    eventDate?: string | null;
    eventLocation?: string | null;
    eventPrice?: string | null;
    eventSpeakers?: Array<{ name: string; title?: string | null; avatar?: string | null }> | null;
  };
  const hasBg = !!c.heroImage;
  return (
    <section
      className="ls-section"
      style={{
        position: 'relative',
        background: hasBg
          ? `linear-gradient(180deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.7) 100%), url(${c.heroImage}) center/cover`
          : 'radial-gradient(ellipse at top, var(--ls-accent-soft) 0%, var(--ls-bg) 100%)',
        minHeight: '80vh',
        color: hasBg ? '#fff' : 'var(--ls-text)',
      }}
    >
      {renderHeroBottomLogo(brand, style, { invertOnDark: hasBg })}
      <div
        className="ls-section-inner"
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 24,
          minHeight: '80vh',
          justifyContent: 'center',
          maxWidth: 980,
        }}
      >
        {renderHeroHeader(
          c.eyebrow ? (
            <div
              style={{
                display: 'inline-block',
                padding: '6px 14px',
                borderRadius: 999,
                background: hasBg ? 'rgba(255,255,255,0.15)' : 'var(--ls-accent-soft)',
                color: hasBg ? '#fff' : 'var(--ls-accent)',
                fontSize: '0.8rem',
                fontWeight: 600,
                letterSpacing: '0.04em',
                backdropFilter: hasBg ? 'blur(8px)' : 'none',
              }}
            >
              {c.eyebrow}
            </div>
          ) : null,
          brand,
          style,
          { invertOnDark: hasBg },
        )}

        <h1
          className="ls-h1"
          style={{
            color: hasBg ? '#fff' : 'var(--ls-text)',
            textShadow: hasBg ? '0 1px 12px rgba(0,0,0,0.5)' : 'none',
            fontSize: 'clamp(2.25rem, 5vw + 1rem, 4.5rem)',
          }}
        >
          {c.headline}
        </h1>

        <Body
          style={{
            color: hasBg ? 'rgba(255,255,255,0.92)' : 'var(--ls-text-muted)',
            maxWidth: 720,
            fontSize: '1.0625rem',
          }}
        >
          {c.subheadline}
        </Body>

        {/* イベント詳細メタ */}
        {(c.eventDate || c.eventLocation || c.eventPrice) && (
          <div
            style={{
              display: 'flex',
              gap: 24,
              flexWrap: 'wrap',
              marginTop: 8,
              padding: '16px 20px',
              background: hasBg ? 'rgba(255,255,255,0.1)' : 'var(--ls-surface)',
              border: `1px solid ${hasBg ? 'rgba(255,255,255,0.18)' : 'var(--ls-border)'}`,
              borderRadius: 'var(--ls-radius-md)',
              backdropFilter: hasBg ? 'blur(8px)' : 'none',
            }}
          >
            {c.eventDate && (
              <EventMeta label="日時" value={c.eventDate} inverted={hasBg} />
            )}
            {c.eventLocation && (
              <EventMeta label="開催地" value={c.eventLocation} inverted={hasBg} />
            )}
            {c.eventPrice && (
              <EventMeta label="料金" value={c.eventPrice} inverted={hasBg} highlight />
            )}
          </div>
        )}

        {/* 登壇者ロー */}
        {c.eventSpeakers && c.eventSpeakers.length > 0 && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 16,
              marginTop: 4,
              flexWrap: 'wrap',
            }}
          >
            <span
              style={{
                fontSize: '0.75rem',
                fontWeight: 600,
                letterSpacing: '0.06em',
                color: hasBg ? 'rgba(255,255,255,0.7)' : 'var(--ls-text-muted)',
                textTransform: 'uppercase',
              }}
            >
              Speakers
            </span>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {c.eventSpeakers.slice(0, 6).map((sp, i) => (
                <SpeakerChip key={i} speaker={sp} inverted={hasBg} />
              ))}
            </div>
          </div>
        )}

        <div className="ls-cta-stack" style={{ marginTop: 16, justifyContent: ctaJustify(style?.ctaAlign) }}>
          <Btn variant="primary" href={c.primaryCtaHref ?? '#'}>
            {c.primaryCta}
          </Btn>
          {c.secondaryCta && (
            <a
              href={c.secondaryCtaHref ?? '#'}
              className="ls-btn"
              style={{
                background: 'transparent',
                color: hasBg ? '#fff' : 'var(--ls-text)',
                border: `1px solid ${hasBg ? 'rgba(255,255,255,0.5)' : 'var(--ls-border)'}`,
              }}
            >
              {c.secondaryCta}
            </a>
          )}
        </div>

        {c.trustLine && (
          <Body
            style={{
              fontSize: '0.85rem',
              opacity: 0.75,
              color: hasBg ? '#fff' : 'var(--ls-text-muted)',
            }}
          >
            {c.trustLine}
          </Body>
        )}
      </div>
    </section>
  );
}

function EventMeta({
  label,
  value,
  inverted,
  highlight,
}: {
  label: string;
  value: string;
  inverted: boolean;
  highlight?: boolean;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
      <span
        style={{
          fontSize: '0.7rem',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: inverted ? 'rgba(255,255,255,0.6)' : 'var(--ls-text-muted)',
          fontWeight: 600,
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: highlight ? '1.125rem' : '1rem',
          fontWeight: highlight ? 700 : 500,
          color: highlight
            ? inverted
              ? 'var(--ls-accent)'
              : 'var(--ls-accent)'
            : inverted
              ? '#fff'
              : 'var(--ls-text)',
        }}
      >
        {value}
      </span>
    </div>
  );
}

function SpeakerChip({
  speaker,
  inverted,
}: {
  speaker: { name: string; title?: string | null; avatar?: string | null };
  inverted: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 12px 6px 6px',
        background: inverted ? 'rgba(255,255,255,0.12)' : 'var(--ls-surface)',
        border: `1px solid ${inverted ? 'rgba(255,255,255,0.2)' : 'var(--ls-border)'}`,
        borderRadius: 999,
        backdropFilter: inverted ? 'blur(8px)' : 'none',
      }}
    >
      {speaker.avatar ? (
        <img
          src={speaker.avatar}
          alt={speaker.name}
          style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover' }}
        />
      ) : (
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            background: 'var(--ls-accent)',
            color: 'var(--ls-accent-text)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.75rem',
            fontWeight: 600,
          }}
        >
          {(speaker.name ?? '?').charAt(0)}
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
        <span
          style={{
            fontSize: '0.85rem',
            fontWeight: 600,
            color: inverted ? '#fff' : 'var(--ls-text)',
          }}
        >
          {speaker.name}
        </span>
        {speaker.title && (
          <span
            style={{
              fontSize: '0.7rem',
              color: inverted ? 'rgba(255,255,255,0.7)' : 'var(--ls-text-muted)',
            }}
          >
            {speaker.title}
          </span>
        )}
      </div>
    </div>
  );
}

export const heroVariants = {
  centered: HeroCentered,
  split: HeroSplit,
  editorial: HeroEditorial,
  image_bg: HeroImageBg,
  event: HeroEvent,
};
